
import { ContentType } from "@/api/scode/scode_params";
import trackingPlanIdEnum from "@/constant/trackingPlanIdEnum";
import PrdVideoCardListCore, { PrdVideoCardListCoreRef } from "@/pages/promote/components/recommend/PrdVideoCardListCore";
import { useCommonContext } from "@/utils/context/common";
import { usePageTitle } from "@/utils/page_utils";
import { getCurrentVcScene } from "@/utils/sceneProvider";
import { setH5ShareCardInfo } from "@/utils/share/common";
import { ProductModel } from "@akc/biz-api";
import { useMount, useUnmount } from "ahooks";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useActivate, useUnactivate } from "react-activation";
import { useSearchParams } from "react-router-dom";
import TopNavBar from "./components/TopNavBar";

/**
 * 商品视频大卡列表页面
 * activitySpuId 商品活动spuId
 * vcScene 推荐场景， "personVideoCard": 逛一逛推荐场景，"videoCard_other_scene"：普通商卡商详进的商品视频大卡场景，"gyg_discover_page": 逛一逛发现页素材商品进的商品视频大卡场景
 * theme 主题色类型，'dark' | 'light'，默认light
 * showVideoBtmGuide 是否展示视频底部引导文案“上滑看更多精彩视频”，1为展示，0为不展示
 * showVideoBottomBar 是否展示视频底部操作栏（加入购物车、立即购买、转发赚钱等），1为展示，0为不展示
 * hideForwardBtn 是否隐藏转发按钮，1为隐藏，0或空为不隐藏
 * materialNo 初始商品素材编号,用于在分享踩链后，踩链者和分享者看到的同一个商品的同一个视频
 * liveNo：本人访问的当前商品所属直播间No,会带到全局的埋点和下单，所以也可以用track.getSuperProperty("live_no")来获取
 * shareLiveNo：别人分享的商品所属直播间No，仅分享踩链时有,仅踩链后直接立构时传给订单用，不会带到全局的埋点和下单
 * previous_resource_type ：上一个页面的资源类型，用于埋点
 */
const VideoCardList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const activitySpuId = searchParams.get("activitySpuId") || "";
  const initMaterialNo = searchParams.get("materialNo") || "";
  // 优先使用url中的vcScene，没有则使用sessionStorage中的vcScene
  const vcSceneRef = useRef<string>(searchParams.get("vcScene") || getCurrentVcScene());
  // todo:已有入口都要加showVideoBtmGuide\showVideoBottomBar参数
  const [showVideoBtmGuide, setShowVideoBtmGuide] = useState(searchParams.get("showVideoBtmGuide") === "1");
  const showVideoBottomBar = searchParams.get("showVideoBottomBar") === "1";
  const hideForwardBtn = searchParams.get("hideForwardBtn") === "1";
  const theme = (searchParams.get("theme") || "light") as "dark" | "light";
  const liveNo = searchParams.get("liveNo") || "";
  const shareLiveNo_of_url = searchParams.get("shareLiveNo") || "";
  // 别人分享的商品所属直播间No，仅分享踩链时有,仅踩链后直接立构当前商品时传给订单用，商品一旦被划走，就要清空
  const [shareLiveNo, setShareLiveNo] = useState<string>(shareLiveNo_of_url || "");
  // url参数传过来的上一个页面的资源类型
  const previous_resource_type_of_url = searchParams.get("previous_resource_type") || "";
  // 当前商品实际的上一个页面的资源类型
  const [previous_resource_type, setPreviousResourceType] = useState<string>(previous_resource_type_of_url || "");
  //分享使用的扩展字段
  const shareContentExtra=useMemo(() => {
    return {
      pageName: "/mx-shop-micro/video-card-list",//用于分享的页面名称，用于分享后的踩链定位
      showVideoBtmGuide:  searchParams.get("showVideoBtmGuide") || "",
      showVideoBottomBar: searchParams.get("showVideoBottomBar") || "",
      hideForwardBtn: searchParams.get("hideForwardBtn") || "",
      theme:  searchParams.get("theme") || "",
      materialNo:""
    };
  }, [searchParams]);
    
  
  const [isBackground, setIsBackground] = useState(false);
  const [notifyFollowProduct, setNotifyFollowProduct] = useState<{
    isFollow?: boolean;
    spuId?: string;
  } | null>(null);

  const [currentProduct, setCurrentProduct] = useState<ProductModel | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [searchRequestId, setSearchRequestId] = useState<string>("");

  const { userVisitInfo, track } = useCommonContext();
  const prdVideoCardListCoreRef = React.useRef<PrdVideoCardListCoreRef>(null);



  usePageTitle("商品视频");


  const handleTopNavBarShare = () => {
    prdVideoCardListCoreRef.current?.showPoster();
  };

  // 点击导航栏购物车
  const handleTopNavBarCart = () => {
    if (track && track.track) {
      track.track("resource_click", {
        page_name: "视频大卡",
        btn_name: "购物车",
        resource_type: "视频",
        resource_rank: currentIndex + 1,
        domain: "视频大卡",
        activity_id: currentProduct?.activityId,
        brand_id: currentProduct?.brandInfo?.brandId,
        activity_spu_code: currentProduct?.activitySpuId,
        sku_id: currentProduct?.selectSkuId,
        material_id: currentProduct?.materialRelationProductInfoVO?.materialNo,
        search_request_id: searchRequestId,
      });
    }
  };

  // 点击导航栏搜索
  const handleTopNavBarSearch = () => {
    if (track && track.track) {
      track.track("resource_click", {
        page_name: "视频大卡",
        btn_name: "搜索",
        resource_type: "视频",
        resource_rank: currentIndex + 1,
        domain: "视频大卡",
        activity_id: currentProduct?.activityId,
        brand_id: currentProduct?.brandInfo?.brandId,
        activity_spu_code: currentProduct?.activitySpuId,
        sku_id: currentProduct?.selectSkuId,
        material_id: currentProduct?.materialRelationProductInfoVO?.materialNo,
        search_request_id: searchRequestId,
      });
    }
  };


  /**
   * 商品视频大卡切换
   * @param product 商品信息
   * @param index 商品索引
   * @param requestId 请求id
   */
  const handleIndexChange = (product: ProductModel, index: number, requestId: string) => {
    if(product?.activitySpuId && product?.activitySpuId===currentProduct?.activitySpuId) {
      return;
    }
    // 仅第一个商品展示引导
    if (index !== 0) {
      setShowVideoBtmGuide(false);
    }
    setCurrentProduct(product);
    setCurrentIndex(index);
    if (requestId) {
      setSearchRequestId(requestId);
    }

  };


  useMount(() => {
    setIsBackground(false);
  });

  useUnmount(() => {
    setIsBackground(true);
  });

  useActivate(() => {
    setIsBackground(false);
    try {
      const info = localStorage.getItem("notifyFollowProduct");
      if (info) {
        console.log("notifyFollowProduct", info);
        setNotifyFollowProduct(JSON.parse(info));
        localStorage.removeItem("notifyFollowProduct");
      }
    } catch (error) {
      console.error("获取 notifyFollowProduct 失败:", error);
    }
  });

  useUnactivate(() => {
    setIsBackground(true);
    setNotifyFollowProduct(null);
  });

  // 监听当前商品变化，动态更新 previous_resource_type,如果当前是路由中传递的商品，则最直接取路由指定的previous_resource_type，如果换成了其他商品，则要重新定义（目前暂未定义，先传空）
  useEffect(() => {
    if (currentProduct?.activitySpuId && currentProduct?.activitySpuId !== activitySpuId) {
      setPreviousResourceType("");
      setShareLiveNo("");
    } else {
      setPreviousResourceType(previous_resource_type_of_url || "");
      setShareLiveNo(shareLiveNo_of_url || "");
    }
  }, [currentProduct, activitySpuId, previous_resource_type_of_url,shareLiveNo_of_url]);

  // 监听商品和previous_resource_type的变化，动态更新系统分享信息
  useEffect(() => {
    if(!currentProduct){
      return;
    }
    // eslint-disable-next-line no-underscore-dangle
    const _shareLiveNo = previous_resource_type === "直播" ? liveNo : undefined; // 如果用户是从直播间进来的，则分享时带上liveNo。其他场景有liveNo也不带
    setH5ShareCardInfo({
      contentType: ContentType.spuProduct,
      contentId: currentProduct.activitySpuId ?? "",
      contentDetailId: currentProduct.selectSkuId ?? "",
      contentTypeConfig: "1",
      contentExtra: {
        ...(shareContentExtra || {}),
        hasVideo: currentProduct.productVideoCardVO?.type==="VIDEO",
        materialNo: currentProduct.productVideoCardVO?.materialNo,
      },
      shortCodeExtend: {
        shareLiveNo: _shareLiveNo,
      },
      posterInfo: {},
      page_name: "视频大卡",
    });
  }, [currentProduct, shareContentExtra,previous_resource_type,liveNo]);

  return (
    <div>
      {/* 自定义导航栏 */}
      <TopNavBar
        onShareClick={handleTopNavBarShare}
        onCartClick={handleTopNavBarCart}
        onSearchClick={handleTopNavBarSearch}
      />

      <PrdVideoCardListCore
          ref={prdVideoCardListCoreRef}
          isBackground={isBackground}
          notifyFollowProduct={notifyFollowProduct || undefined}
          activitySpuId={activitySpuId}
          initMaterialNo={initMaterialNo}
          vcScene={vcSceneRef.current}
          showVideoBtmGuide={showVideoBtmGuide}
          showVideoBottomBar={showVideoBottomBar}
          hideForwardBtn={hideForwardBtn}
          theme={theme}
          liveNo={liveNo}
          shareLiveNo={shareLiveNo}
          trackDataCommon={{
            page_name: "视频大卡",
            previous_page_name:searchParams.get("previous_page_name") || "",
            domain: "视频大卡",
            tracking_plan_ids: {
              add_to_cart: trackingPlanIdEnum.productVideoListPage_skuPop_add_to_cart,
              add_cart_click: trackingPlanIdEnum.productVideoListPage_bottomBar_cartIcon_resource_click,
              buy_now_click: trackingPlanIdEnum.productVideoListPage_bottomBar_immediatelyBtn_resource_click
            }
          }}
          previous_resource_type={previous_resource_type}
          previous_resource_type_of_url={previous_resource_type_of_url}
          shareContentExtra={shareContentExtra}
          onIndexChange={handleIndexChange}
        />
    </div>
  );
};

export default VideoCardList;
